import { Menu } from '@headlessui/react';
import { ChevronDownIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { ArrowRight, Bot } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { formatPhoneNumberForDisplay } from '@/components/correspondence/transmission/validation';
import { useUploadManagerUnified } from '@/components/documents-v2/hooks/useUploadManagerUnified';
import { DocumentSelectionModal } from '@/components/tasks/task-detail/DocumentSelectionModal';
import { Badge, BadgeColor } from '@/components/ui/catalyst/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useMatter } from '@/hooks/useMatter';
import {
  TreatmentRecordRequestSchema,
  TreatmentRecordRequestStatus,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { getExtendedMetadata } from '@/types/treatment-record-request-extended';

import { StatusOptions } from '../../constants';

export const StatusToLabel: Record<
  TreatmentRecordRequestStatus,
  { label: string; color: BadgeColor }
> = {
  not_ready: {
    label: 'Not Ready',
    color: 'zinc', // neutral gray
  },
  pending: {
    label: 'Ready',
    color: 'yellow', // waiting to start
  },
  request_submitted: {
    label: 'Sent',
    color: 'blue', // action taken
  },
  request_received: {
    label: 'Confirmed',
    color: 'sky', // received, but not yet processed
  },
  awaiting_invoice: {
    label: 'Invoice Pending',
    color: 'orange', // waiting for invoice
  },
  pending_payment: {
    label: 'Invoice Ready',
    color: 'amber', // payment step
  },
  records_pending: {
    label: 'Records Pending',
    color: 'violet', // waiting for records
  },
  records_delivered: {
    label: 'Delivered',
    color: 'green', // success, delivered
  },
  records_ingested: {
    label: 'Processed',
    color: 'emerald', // final success, processed
  },
  rejected: {
    label: 'Rejected',
    color: 'red', // error
  },
  cancelled: {
    label: 'Cancelled',
    color: 'rose', // cancelled, distinct from error
  },
  auto_retried: {
    label: 'Auto-Retried',
    color: 'zinc', // muted/grey, terminal state like cancelled
  },
};

/**
 * Tooltip content for auto-retried requests
 * Displays information about why the request was auto-retried by Voice AI
 */
interface AutoRetriedTooltipContentProps {
  metadata: ReturnType<typeof getExtendedMetadata>;
  matterId: string;
  providerId?: string;
}

const AutoRetriedTooltipContent = ({
  metadata,
  matterId,
  providerId,
}: AutoRetriedTooltipContentProps) => {
  const alternateContact = metadata.alternate_contact;
  const forkedToRequestId = metadata.forked_to_request_id;

  // Format the fax number if it's a fax contact
  const formattedValue =
    alternateContact?.handle_type === 'fax' && alternateContact?.value
      ? formatPhoneNumberForDisplay(alternateContact.value)
      : alternateContact?.value;

  return (
    <div className="space-y-2 max-w-xs">
      <p className="font-medium">Auto-Retried</p>
      <p className="text-xs opacity-80">
        Voice AI discovered a different fax number during a provider call.
      </p>
      {formattedValue && (
        <p className="text-xs">
          <span className="opacity-70">New Fax:</span>{' '}
          <span className="font-medium">{formattedValue}</span>
        </p>
      )}
      {forkedToRequestId && providerId && (
        <Link
          to={`/matters/${matterId}/treatment?request_id=${forkedToRequestId}&provider_id=${providerId}`}
          className="inline-flex items-center gap-1 text-xs text-blue-300 hover:text-blue-200 font-medium"
        >
          <ArrowRight className="h-3 w-3" />
          View new request
        </Link>
      )}
    </div>
  );
};

export const RequestStatusBadge = ({
  status,
  metadata,
  matterId,
  providerId,
}: {
  status: TreatmentRecordRequestStatus;
  metadata?: unknown;
  matterId?: string;
  providerId?: string;
}) => {
  const label = StatusToLabel[status];
  const extendedMetadata = getExtendedMetadata(metadata);

  // For auto_retried status, show a tooltip with details
  if (status === 'auto_retried' && matterId) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge color={label.color} className="cursor-help">
              {label.label}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="p-3">
            <AutoRetriedTooltipContent
              metadata={extendedMetadata}
              matterId={matterId}
              providerId={providerId}
            />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return <Badge color={label.color}>{label.label}</Badge>;
};

export const InteractiveRequestStatusBadge = ({
  request,
  providerId,
  className,
  showMenuIcon = true,
}: {
  request: TreatmentRecordRequestSchema;
  providerId: string;
  className?: string;
  showMenuIcon?: boolean;
}) => {
  const { matterId } = useMatter();
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [changeStatus] =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsChangeStatusCreateMutation();

  // TRR attachment requirement state: holds the pending status while waiting for file upload
  const [trrAttachmentModalOpen, setTrrAttachmentModalOpen] = useState(false);
  const pendingTrrStatusRef = useRef<{
    status: TreatmentRecordRequestStatus;
  } | null>(null);

  // Upload manager for the TRR attachment modal
  const { handleUploadToCollection: handleTrrUpload } =
    useUploadManagerUnified({
      matterId: matterId ?? '',
      refetch: () => {},
    });

  /**
   * Execute the actual status change API call, optionally including attachments.
   * Does NOT handle toasts or errors — callers are responsible for their own
   * success/error messaging so that each flow can show the appropriate toast.
   */
  const executeStatusChange = useCallback(
    async (
      newStatus: TreatmentRecordRequestStatus,
      attachments?: string[],
    ) => {
      setIsChangingStatus(true);
      try {
        await changeStatus({
          matterId,
          providerId,
          requestId: request.id,
          changeTreatmentRecordRequestStatusSchema: {
            new_status: newStatus,
            override_requirements: true,
            ...(attachments ? { attachments } : {}),
          },
        }).unwrap();
      } finally {
        setIsChangingStatus(false);
      }
    },
    [changeStatus, matterId, providerId, request.id],
  );

  /**
   * Callback invoked by the DocumentSelectionModal when the user selects
   * (or uploads) files for a TRR completion.
   */
  const handleTrrAttachmentSelect = useCallback(
    async (blobIds: string[]) => {
      const pending = pendingTrrStatusRef.current;
      if (!pending) {
        return;
      }
      if (blobIds.length === 0) {
        toast.error(
          'Please select at least one file to complete this request.',
        );
        return;
      }

      try {
        await executeStatusChange(pending.status, blobIds);
        toast.success('Medical records uploaded and request completed');
        pendingTrrStatusRef.current = null;
      } catch {
        toast.error('Failed to complete request. Please try again.');
        throw new Error('Failed to complete request');
      }
    },
    [executeStatusChange],
  );

  const handleStatusChange = async (
    newStatus: TreatmentRecordRequestStatus,
  ) => {
    if (newStatus === request.status) {
      toast.info('Already at this status');
      return;
    }

    // When completing a TRR (records_ingested), open the document selection modal
    // so the user can upload/select the medical record files.
    if (newStatus === 'records_ingested') {
      pendingTrrStatusRef.current = { status: newStatus };
      setTrrAttachmentModalOpen(true);
      return;
    }

    try {
      await executeStatusChange(newStatus);
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    }
  };

  const label = StatusToLabel[request.status || 'not_ready'];
  const automationState = request.metadata?.automation_state_v2;
  const isBlocked = automationState?.status === 'blocked';

  const getBotIconColor = () => {
    if (isBlocked) return 'text-amber-500';
    if (automationState?.status === 'running') return 'text-blue-500';
    return 'text-blue-400';
  };

  if (!request.status) return null;

  return (
    <>
      <Menu as="div" className="relative inline-block">
        <Menu.Button as="div" className="cursor-pointer">
          <Badge
            color={label.color}
            className={`inline-flex items-center gap-1.5 ${className ?? ''}`}
          >
            <span>{label.label}</span>
            {automationState && automationState.status !== 'idle' && (
              <>
                <span className="opacity-50">•</span>
                <Bot className={`h-2.5 w-2.5 ${getBotIconColor()}`} />
              </>
            )}
            {showMenuIcon ? (
              <ChevronDownIcon className="h-2.5 w-2.5 opacity-60" />
            ) : (
              <EllipsisHorizontalIcon className="h-2.5 w-2.5 opacity-50" />
            )}
          </Badge>
        </Menu.Button>

        <Menu.Items className="absolute right-0 mt-1 z-50 w-48 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">
            Jump to status
          </div>
          <div className="py-1">
            {StatusOptions.map((statusOption) => (
              <Menu.Item key={statusOption.value}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => handleStatusChange(statusOption.value)}
                    disabled={
                      isChangingStatus || statusOption.value === request.status
                    }
                    className={`${active ? 'bg-gray-50' : ''} ${
                      statusOption.value === request.status
                        ? 'text-gray-400'
                        : 'text-gray-700'
                    } flex w-full items-center px-3 py-2 text-sm disabled:cursor-not-allowed`}
                  >
                    {statusOption.label}
                    {statusOption.value === request.status && (
                      <span className="ml-auto text-xs text-gray-400">
                        Current
                      </span>
                    )}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Menu>

      {/* TRR Medical Records Upload Modal */}
      {matterId && (
        <DocumentSelectionModal
          open={trrAttachmentModalOpen}
          onClose={() => {
            setTrrAttachmentModalOpen(false);
            pendingTrrStatusRef.current = null;
          }}
          matterId={matterId}
          onSelectDocuments={handleTrrAttachmentSelect}
          onUpload={handleTrrUpload}
          description="Upload Record Set: To mark this request as complete, please upload or select the medical record files."
          requireSelection
        />
      )}
    </>
  );
};
